import { useCallback } from 'react';
import { useMapUiActions } from '@/stores/map/mapUiStore';
import { useLocationSharingActions } from '@/stores/map/locationSharingStore';
import { getUserStatus } from '@/context_providers/group_user_context/OrgGroupUserStatusStore';

/**
 * Hook to provide functionality for moving map to a user's location
 * @returns {Object} An object containing the moveMapToUser function
 */
export const useMapToUser = () => {
  const mapUiActions = useMapUiActions();
  const locationSharingActions = useLocationSharingActions();

  /**
   * Moves the map to a user's location and focuses on their marker
   * @param {string} userAuthId - The auth ID of the user to move the map to
   * @param {number} [zoomLevel=18] - Optional zoom level to set (default: 18)
   * @returns {boolean} - Whether the map was moved (true if successful, false if no valid location)
   */
  const moveMapToUser = useCallback(
    (userAuthId, zoomLevel = 18) => {
      const userStatusData = getUserStatus(userAuthId);

      if (
        userStatusData?.userStatus &&
        userStatusData.userStatus.shouldShowPinOnMap()
      ) {
        // Move map to user location
        const latLong = {
          lat: userStatusData.userStatus.location.latitude,
          lng: userStatusData.userStatus.location.longitude,
        };

        mapUiActions.setMapCenter(latLong);
        mapUiActions.setMapZoom(zoomLevel);

        // Open the popover on the user's marker
        locationSharingActions.setSelectedLocationMarkerByUserId(userAuthId);

        return true;
      }

      return false;
    },
    [mapUiActions, locationSharingActions],
  );

  return { moveMapToUser };
};

export default useMapToUser;
